// Fluxo multifocal (medição presencial em loja) — sem endpoint de metafield
// exposto ao client, busca /pages/nossas-lojas e extrai o JSON embutido.
// Haversine e link de WhatsApp portados de assets/stores-locations-page-new.js.

const STORES_PAGE_URL = '/pages/nossas-lojas';
const DEFAULT_PHONE_BR_WITHOUT_DDI = '2123919797';

/**
 * @typedef {object} Store
 * @property {string} id
 * @property {string} title
 * @property {string} city
 * @property {string} state
 * @property {string} address
 * @property {string} image
 * @property {string} wpp_contact
 * @property {string} opening_hours
 * @property {string} latitude
 * @property {string} longitude
 */

/**
 * @typedef {object} NoStoresFound
 * @property {string} headline
 * @property {string} title
 * @property {string} description
 * @property {{ text: string, link: string }} button1
 * @property {{ text: string, link: string }} button2
 */

/**
 * @typedef {object} StoresData
 * @property {Store[]} stores
 * @property {NoStoresFound | null} noStoresFound
 */

/** @returns {Promise<StoresData | null>} */
async function fetchStoresData() {
  try {
    const response = await fetch(STORES_PAGE_URL);
    if (!response.ok) return null;
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector(
      'stores-locations-page-new script[type="application/json"]'
    );
    if (!script) return null;
    const data = JSON.parse(script.textContent || '{}');
    if (!Array.isArray(data.stores)) return null;
    return { stores: data.stores, noStoresFound: data.noStoresFound || null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[QuickAddStores] Erro ao buscar lojas:', error);
    return null;
  }
}

/** @type {Promise<StoresData | null> | null} */
let storesPromise = null;

/**
 * Cacheia por sessão; falha pontual não bloqueia tentativas seguintes.
 * @returns {Promise<StoresData | null>}
 */
export function getCachedStoresData() {
  if (!storesPromise) {
    storesPromise = fetchStoresData().then(result => {
      if (!result) storesPromise = null;
      return result;
    });
  }
  return storesPromise;
}

/** @param {number} degrees */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Idêntico a assets/stores-locations-page-new.js#calculateDistance.
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} km
 */
export function haversineDistanceKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * @param {Store[]} stores
 * @param {number} userLat
 * @param {number} userLon
 * @returns {Array<Store & { distanceKm: number }>} ordenado por distância — só lojas com coordenada válida.
 */
export function sortStoresByDistance(stores, userLat, userLon) {
  return stores
    .map(store => {
      const lat = parseFloat(store.latitude);
      const lon = parseFloat(store.longitude);
      if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
      return {
        ...store,
        distanceKm: haversineDistanceKm(userLat, userLon, lat, lon),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

/** @param {number} km */
export function formatDistanceKm(km) {
  return `a ${km.toFixed(1).replace('.', ',')} km`;
}

/**
 * Idêntico a assets/stores-locations-page-new.js — normaliza telefone e usa
 * telefone padrão quando faltar wpp_contact.
 * @param {string | null | undefined} rawPhone
 * @returns {string}
 */
export function buildWhatsappLink(rawPhone) {
  const raw =
    rawPhone && rawPhone.trim() ? rawPhone : DEFAULT_PHONE_BR_WITHOUT_DDI;
  let digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }
  return `https://wa.me/55${digits}`;
}

/**
 * Prioriza lat/long; cai pro endereço em texto se faltar coordenada.
 * @param {Store} store
 * @returns {string}
 */
export function buildDirectionsLink(store) {
  const lat = parseFloat(store.latitude);
  const lon = parseFloat(store.longitude);
  const destination =
    !Number.isNaN(lat) && !Number.isNaN(lon)
      ? `${lat},${lon}`
      : `${store.address || ''} ${store.city || ''} ${store.state || ''}`.trim();
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}
